import React from "react";

/**
 * ToastBanner - 인라인 알림 배너 컴포넌트
 * 고정 위치가 아닌 일반 flow에 배치되며, 부모가 위치를 결정함
 *
 * Props:
 * - visible: boolean - 표시 여부
 * - variant: 'warning' | 'error' | 'info' - 배너 종류 (기본: 'warning')
 * - message: string - 메인 메시지
 * - subMessage: string (선택) - 보조 메시지
 * - onDismiss: function (선택) - 닫기 버튼 클릭 핸들러
 */
const ToastBanner = ({ visible, variant = "warning", message, subMessage, onDismiss }) => {
    // visible이 false면 렌더링하지 않음
    if (!visible) return null;

    // variant별 스타일 매핑
    const variantStyles = {
        warning: "bg-warning-subtle border-warning/30 text-warning",
        error: "bg-loss-subtle border-loss/30 text-loss",
        info: "bg-brand-subtle border-brand/30 text-brand-light",
    };

    const containerClass = variantStyles[variant] || variantStyles.warning;

    return (
        <div
            role={variant === 'error' ? 'alert' : 'status'}
            className={`rounded-lg border px-4 py-2.5 animate-slide-down ${containerClass}`}
        >
            <div className="flex items-start justify-between gap-3">
                {/* 좌측: 아이콘 + 메시지 */}
                <div className="flex items-start gap-2 min-w-0">
                    {/* 경고 아이콘 */}
                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                        className="h-4 w-4 flex-shrink-0 mt-0.5"
                    >
                        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>

                    {/* 메시지 영역 */}
                    <div className="min-w-0">
                        <p className="text-sm font-medium leading-snug">{message}</p>
                        {/* 보조 메시지가 있을 때만 표시 */}
                        {subMessage && (
                            <p className="text-xs opacity-80 mt-0.5">{subMessage}</p>
                        )}
                    </div>
                </div>

                {/* 우측: 닫기 버튼 (onDismiss가 있을 때만 표시) */}
                {onDismiss && (
                    <button
                        onClick={onDismiss}
                        className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
                        aria-label="닫기"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            className="h-4 w-4"
                        >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
};

export default ToastBanner;
